import React, {Component, ReactElement, useEffect, useRef, useState} from "react";
import {PathComponent} from "index";
import {colorRed, lightGray} from "@/shared/colors";
import {StyledBadge2, StyledBoxColumn, StyledBoxRow, StyledSpan} from "@/shared/StyledComponents";
import {BORDER_RADIUS, DIVIDER, PD_LG, PD_MD, PD_SM, PD_XSM, SZ_SM} from "@/shared/dimens";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  SvgIcon,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import {BadgeSize, ListItemChange, OnListItemsListener, UserCache, UserProfilePhoto} from "@/shared/types";
import {UserFragment} from "@/shared/UserFragment";
import {Auth} from "@firebase/auth";
import {PathProps} from "@/shared";
import {BaseApp} from "../shared/BaseApp";
import {Close, MenuOutlined, NotificationsOutlined} from "@mui/icons-material";
import {Module, MODULE_CATEGORY_KTS, ModuleCategory, MODULES} from "../app-modules/modules";
import {Outlet} from "react-router-dom";
import {Notification, NotificationsContextTrait, NotificationsSource} from "@/libwyze-shared/types";
import {PracticeSelect} from "../libwyze-shared/components/PracticeSelect";
import {BaseMain, BaseMainProps, BaseMainState} from "../libwyze-shared/components/BaseMain";
import {appType} from "../libwyze-common/types";

enum DrawerItemType {
  TITLE,
  LINK,
}

type DrawerItem = {
  type: DrawerItemType,
  text: string,
  iconUrl?: string,
  iconType?: typeof SvgIcon,
  url?: string,
}

function createDrawerItems(): DrawerItem[] {
  const items: DrawerItem[] = [];
  for (const categoryName of Object.getOwnPropertyNames(ModuleCategory)) {
    const category = ModuleCategory[categoryName];
    items.push({
      type: DrawerItemType.TITLE,
      text: MODULE_CATEGORY_KTS.findText(category),
    });
    for (const module of MODULES.filter(module => module.category === category)) {
      items.push({
        type: DrawerItemType.LINK,
        text: module.text,
        iconType: module.iconType,
        url: `/${module.name}`,
      });
    }
  }
  return items;
}

const DRAWER_ITEMS: DrawerItem[] = createDrawerItems();

class NotificationsJewel extends Component<{ path: PathProps }, {
  source?: NotificationsSource,
  count?: number,
}> {
  constructor(props: { path: PathProps }, context: any) {
    super(props, context);
    this.state = {}
  }

  setSource(source: NotificationsSource | null): void {
    this.setState({
      source,
    })
  }

  setCount(count: number): void {
    this.setState({
      count,
    });
  }

  render(): ReactElement {
    const source = this.state.source;
    const count = this.state.count;
    return <Tooltip title={"Notifications"}>
      <Button
        variant={this.props.path.location.pathname.startsWith("/notifications") ? "contained" : null}
        style={{position: "relative", overflow: "auto", minWidth: 0, padding: PD_SM}}
        onClick={(event) => source?.showPopup(event.target as HTMLElement)}>
        <NotificationsOutlined/>
        {count > 0
          ? <StyledBadge2
            selected
            backgroundColor={colorRed}
            boxStyle={{position: "absolute", top: 0, right: 0}}
            size={BadgeSize.XSMALL}
          >
            {count}
          </StyledBadge2>
          : null}
      </Button>
    </Tooltip>;
  }
}

const elementsLogo = <img src={"/images/dentwyze_elements_logotype.svg"}
                          style={{height: 48, marginBottom: -4, marginLeft: -12}}/>;


function MainTitleBar(props: {
  auth?: Auth,
  path: PathProps,
  selectedModule: Module,
  practiceSelect: ReactElement,
}): ReactElement {
  const buttonRef = useRef<HTMLButtonElement>();
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const notificationsJewelRef = useRef<NotificationsJewel>();
  useEffect(() => {
    const notificationsSource = BaseApp.CONTEXT.getTrait<NotificationsContextTrait>().getNotificationsSource();
    const observer = new class implements OnListItemsListener<Notification> {
      onItemChanged(item: Notification, change: ListItemChange): void {
        notificationsJewelRef.current?.setCount(notificationsSource.getNotificationsCount());
      }
    }
    notificationsJewelRef.current?.setSource(notificationsSource);
    notificationsSource?.registerObserver(observer);
    return () => {
      notificationsJewelRef.current?.setSource(null);
      notificationsSource?.unregisterObserver(observer);
    }
  }, []);
  const theme = useTheme();
  const md = theme.breakpoints.up("md");
  const titleElement = elementsLogo;
  return <><StyledBoxRow style={{
    flexGrow: 1,
    alignItems: "center",
  }}>
    <a href={"/"}>
      <StyledBoxRow style={{alignItems: "center"}}>
        <img src={md ? `/images/${appType}_logotype.svg` : `/images/${appType}_logotype_sm.svg`}
             style={{display: "block", height: 32}}/>
        {typeof titleElement === "string"
          ? <Typography variant="h5">{titleElement}</Typography>
          : titleElement}
      </StyledBoxRow>
    </a>
    <Button
      ref={buttonRef}
      style={{padding: `${PD_XSM}px ${PD_MD}px`}}
      startIcon={<MenuOutlined/>}
      onClick={() => {
        setDrawerOpen(true);
      }}
    >
      <StyledBoxRow style={{alignItems: "center", color: theme.palette.text.primary, borderRadius: BORDER_RADIUS}}>
        <Typography style={{fontWeight: "bold"}}>{props.selectedModule.text}</Typography>
      </StyledBoxRow>
    </Button>
    <StyledSpan/>
    {props.practiceSelect}
    <StyledBoxRow style={{gap: 4, alignItems: "center"}}>
      {/*{accountAccessory}*/}
      <NotificationsJewel path={props.path} ref={notificationsJewelRef}/>
      <Card style={{marginLeft: PD_SM, width: SZ_SM, height: SZ_SM, flexShrink: 0}}>
        <ButtonBase
          onClick={() => BaseApp.CONTEXT.showDialog(
            null,
            () => <UserFragment uid={props.auth.currentUser.uid}/>)}>
          <img src={UserProfilePhoto(UserCache.getInstance().getCachedUser(props.auth.currentUser.uid))}
               style={{width: "100%", height: "100%"}}/>
        </ButtonBase>
      </Card>
    </StyledBoxRow>
  </StyledBoxRow>
    <Drawer open={drawerOpen} anchor="left" onClose={() => setDrawerOpen(false)}>
      <Box sx={{width: 320}} role="presentation" onClick={() => setDrawerOpen(false)}>
        <Box style={{
          display: "flex",
          right: PD_MD,
          top: PD_LG,
          alignItems: "center",
          height: SZ_SM,
          position: "absolute",
        }}>
          <IconButton style={{
            width: SZ_SM,
            height: SZ_SM,
            background: lightGray,
          }} onClick={() => setDrawerOpen(false)}>
            <Close style={{color: "black"}}/>
          </IconButton>
        </Box>
        <StyledBoxColumn style={{gap: PD_MD}}>
          <img
            src={"/images/dentwyze_logotype.svg"}
            style={{
              alignSelf: "flex-start",
              marginTop: PD_LG,
              marginLeft: PD_MD,
              height: 48,
              paddingTop: 8,
              paddingBottom: 8,
            }}/>
          <List>
            {DRAWER_ITEMS.map(item => {
              switch (item.type) {
                case DrawerItemType.TITLE:
                  return <Typography style={{
                    borderTop: DIVIDER,
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    padding: PD_MD,
                    color: theme.palette.primary.main
                  }}>{item.text}</Typography>
                case DrawerItemType.LINK:
                  return <ListItem disablePadding>
                    <ListItemButton onClick={() => {
                      // We force reload page here to allow any App-scoped initialization to happen for this module.
                      window.location.href = item.url;
                    }}>
                      <StyledBoxRow style={{paddingLeft: PD_MD, height: 32, alignItems: "center"}}>
                        {item.iconType
                          ? <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyItems: "center",
                            width: 28,
                            height: 28,
                            borderRadius: BORDER_RADIUS
                          }}>
                            <item.iconType style={{width: 24, height: 24}}/>
                          </div>
                          : null}
                        {item.iconUrl
                          ? <img src={item.iconUrl} style={{width: 28, height: 28, borderRadius: BORDER_RADIUS}}/>
                          : null}
                        <Typography variant="body1" style={{fontWeight: "bold"}}>{item.text}</Typography>
                      </StyledBoxRow>
                    </ListItemButton>
                  </ListItem>;
              }
              return null;
            })}
          </List>
        </StyledBoxColumn>
      </Box>
    </Drawer>
  </>;
}

export type MainProps = BaseMainProps & {}

type MainState = BaseMainState & {}

export class Main extends BaseMain<MainProps, MainState> {

  static nestedPaths(): PathComponent[] {
    return MODULES.map(module => ({
      path: module.name,
      render: () => <Outlet/>,
      // @ts-ignore
      children: module.MainFragment.nestedPaths() as PathComponent[],
    } as PathComponent));
  }

  protected renderContent(): ReactElement {
    const pathParts = this.props.path.location.pathname.split("/").filter(Boolean);
    const selectedModule = MODULES.find(module => module.name === pathParts[0]) || MODULES[0];
    return <>
      <StyledBoxRow style={{
        padding: PD_MD,
        alignItems: "center",
        height: 72,
        justifyContent: "center",
        background: this.theme.palette.background.default,
        // background: `linear-gradient(${theme.palette.grey[100]},${theme.palette.grey[300]})`,
        borderBottom: DIVIDER,
      }}>
        <MainTitleBar
          auth={this.auth}
          path={this.props.path}
          selectedModule={selectedModule}
          practiceSelect={<PracticeSelect
            practices={[this.state.practicePage.practicePageProfile]}
            selectedPractice={this.state.practicePage.practicePageProfile}
            setSelectedPractice={null}
            linkPractice={null}/>
          }
        />
      </StyledBoxRow>
      {selectedModule
        ? <selectedModule.MainFragment path={this.props.path} modulePath={selectedModule.name}/>
        : null}
    </>;
  }
}